import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import classNames from 'classnames';
import { Modal, Panel, PanelGroup, FloatingAction, designSystem } from '@yola/ws-ui';
import useMatchBreakpoint from 'src/js/modules/utils/custom-hooks/use-match-breakpoint';
import segment from 'src/js/modules/analytics/segment';
import PageManagerTemplateItem from './template-item';
import PageManagerTreeView from './tree-view';
import stopPropagation from '../../utils/stop-propagation';
import LimitBanner from '../../common/components/limit-banner';
import { ROOT_ID } from '../constants/index.js';

const {
  Search,
  Stack,
  Heading,
  Paragraph,
  Box,
  HorizontalStack,
  ActionButton,
  DialogHeader,
  DialogHeaderTitle,
  DialogHeaderControls,
  DialogHeaderIconButton,
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  ActionList,
  ActionListItem,
  ActionListDivider,
  IconButton,
  Checkbox,
  Tooltip,
} = designSystem;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

// we need cancel selector for tablets to make work controls
const DRAG_CANCEL_SELECTOR = `
      .ws-ui-dialog-header-control
    `;
const SHOW_FULLSCREEN_BUTTON_BREAKPOINT = '600px';
const UPGRADE_BANNER_SIZE = 80;
// const FLOATING_BUTTON_SIZE = 48;
const UPGRADE_BANNER_SIZE_WITHOUT_PROGRESS_BARR = 50;
const MODAL_WIDTH = 360;
const MODAL_HEIGHT = 533;
const MODAL_HEIGHT_MIN = 250;

const DEFAULT_ITEMS = [];
const DEFAULT_OPTIONS_TO_CREATE_PAGE = [];

const PageManager = ({
  items = DEFAULT_ITEMS,
  captions,
  onClose,
  onItemClick,
  onTemplateItemClick,
  upgradeBanner = null,
  optionsToCreatePage = DEFAULT_OPTIONS_TO_CREATE_PAGE,
  onAddNewPage,
  // TODO: Uncomment when folder creation will be implemented
  // eslint-disable-next-line no-unused-vars
  onCreateFolder,
  styles = null,
  className = null,
  isPageCreationAvailable = false,
  filter,
  onSearchClick = Function.prototype,
  bulkPagesActions = [],
  isAllItemsSelected = false,
  selectedItemsMap = {},
  onItemSelect = Function.prototype,
  onAllItemsSelect = Function.prototype,
  onCancelSelection = Function.prototype,
}) => {
  const isFullscreenButtonVisible = useMatchBreakpoint(SHOW_FULLSCREEN_BUTTON_BREAKPOINT);
  const [isFullscreen, setIsFullscreen] = useState(bowser.mobile);
  const [selectModeEnabled, setSelectModeEnabled] = useState(false);

  const portalRootNode = document.querySelector('.ws-page-manager-modal');
  const selectedItemsCount = useMemo(
    () => Object.values(selectedItemsMap).filter(Boolean).length,
    [selectedItemsMap]
  );

  const [isSelectionBarBorderVisible, setIsSelectionBarBorderVisible] = useState(false);
  const modalContentRef = useRef(null);

  const selectionBarClasses = classNames('ws-page-manager__selection-bar', {
    'ws-page-manager__selection-bar--with-border': isSelectionBarBorderVisible,
  });

  const onSearchFocus = () => {
    onSearchClick();
  };

  const onSetSizeModal = () => {
    setIsFullscreen((prevState) => !prevState);
  };

  const onClear = () => {
    filter(null);
  };

  const onChangeQuery = (value) => {
    filter(value);
  };

  const onSelectModeToggle = () => {
    setSelectModeEnabled((prev) => !prev);

    if (selectModeEnabled) {
      onCancelSelection();
    } else {
      trackEvent(events.PAGE_MANAGER_BULK_EDITING_SELECTED);
    }
  };

  const onModalCancel = () => {
    if (selectModeEnabled) {
      setSelectModeEnabled(false);
      onCancelSelection();

      return;
    }

    onClose();
  };

  const floatingButtonOffsetY = upgradeBanner
    ? 24 +
      (upgradeBanner.withProgressBar
        ? UPGRADE_BANNER_SIZE
        : UPGRADE_BANNER_SIZE_WITHOUT_PROGRESS_BARR)
    : 24;

  const isBulkPagesItemDisabled = selectedItemsCount === 0;

  useEffect(() => {
    const handleScroll = () => {
      if (modalContentRef.current) {
        setIsSelectionBarBorderVisible(modalContentRef.current.scrollTop > 0);
      }
    };

    const el = modalContentRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <div className={classNames('ws-page-manager', className)} style={styles}>
      <Modal
        width={MODAL_WIDTH}
        height={MODAL_HEIGHT}
        minHeight={MODAL_HEIGHT_MIN}
        className="ws-page-manager-modal"
        offsetX={100}
        offsetY={50}
        fullscreen={isFullscreen}
        dragHandleSelector=".ws-page-manager__header"
        floatingModalElementSelector=".ws-ui-action-list-item"
        dragCancelSelector={DRAG_CANCEL_SELECTOR}
        handleCancel={onModalCancel}
        handleSubmit={onClose}
        overlay="none"
      >
        <PanelGroup height="100%">
          <Panel corners="squared" className="ws-page-manager__header">
            <DialogHeader>
              <DialogHeaderTitle>{captions.title}</DialogHeaderTitle>
              <DialogHeaderControls>
                {isFullscreenButtonVisible && (
                  <DialogHeaderIconButton
                    label={captions.fullscreenTooltip}
                    glyph={isFullscreen ? 'minimize' : 'maximize'}
                    iconStrokeWidth={2}
                    onClick={onSetSizeModal}
                    onMouseDown={stopPropagation}
                  />
                )}
                <DialogHeaderIconButton
                  glyph="close"
                  onClick={onClose}
                  onMouseDown={stopPropagation}
                />
              </DialogHeaderControls>
            </DialogHeader>
          </Panel>

          <Panel
            className="ws-page-manager__content"
            height="100%"
            scrollable
            rootRef={modalContentRef}
          >
            <div className="ws-page-manager__search">
              <Box
                paddingLeft="spacing-m"
                paddingRight="spacing-m"
                paddingTop="spacing-2-xs"
                paddingBottom="spacing-2-xs"
              >
                <Search
                  transparent
                  placeholder={captions.searchPlaceholder}
                  onFocus={onSearchFocus}
                  onChange={onChangeQuery}
                  onClear={onClear}
                />
              </Box>
            </div>
            <div className={selectionBarClasses}>
              <Box
                paddingLeft="spacing-m"
                paddingRight="spacing-m"
                paddingTop="spacing-3-xs"
                paddingBottom="spacing-3-xs"
              >
                <HorizontalStack align="space-between">
                  {selectModeEnabled ? (
                    <Checkbox
                      id="all-items"
                      label={
                        <Paragraph appearance="medium-emphasis">{`${selectedItemsCount} ${captions.selected}`}</Paragraph>
                      }
                      checked={isAllItemsSelected}
                      onChange={onAllItemsSelect}
                      value="all-items"
                      isLabelBold={false}
                      size="small"
                      indeterminate={selectedItemsCount > 0 && !isAllItemsSelected}
                    />
                  ) : (
                    <Paragraph appearance="medium-emphasis">{captions.pages}</Paragraph>
                  )}

                  <HorizontalStack gap="spacing-3-xs" verticalAlign="center">
                    {selectModeEnabled && (
                      <Dropdown placement="bottom-right" portalRootNode={portalRootNode}>
                        <DropdownTrigger>
                          <IconButton
                            transparent
                            glyph="more-2"
                            size="medium"
                            appearance="accent"
                          />
                        </DropdownTrigger>
                        <DropdownContent>
                          <ActionList
                            withMobileBorder
                            data-for="bulk-pages-actions-tooltip"
                            data-tip
                          >
                            {bulkPagesActions.map((action) => (
                              <React.Fragment key={action.id}>
                                {action.divider === 'before' && <ActionListDivider />}
                                <ActionListItem
                                  title={action.title}
                                  iconGlyph={action.iconGlyph}
                                  iconPlacement="left"
                                  disabled={isBulkPagesItemDisabled}
                                  {...(action.iconType && { iconType: action.iconType })}
                                  {...(action.component && { component: action.component })}
                                  {...(!isBulkPagesItemDisabled && { onClick: action.onClick })}
                                />
                                {action.divider === 'after' && <ActionListDivider />}
                              </React.Fragment>
                            ))}
                          </ActionList>
                          {isBulkPagesItemDisabled && (
                            <div className="ws-page-manager__tooltip-wrapper">
                              <Tooltip
                                id="bulk-pages-actions-tooltip"
                                placement="top"
                                text={captions.noItemsSelectedTooltip}
                              />
                            </div>
                          )}
                        </DropdownContent>
                      </Dropdown>
                    )}
                    <ActionButton
                      format="text"
                      label={selectModeEnabled ? captions.cancel : captions.select}
                      onClick={onSelectModeToggle}
                    />
                  </HorizontalStack>
                </HorizontalStack>
              </Box>
            </div>
            {items.length ? (
              <React.Fragment>
                <PageManagerTreeView
                  rootId={ROOT_ID}
                  items={items}
                  onItemClick={onItemClick}
                  selectModeEnabled={selectModeEnabled}
                  selectedItemsMap={selectedItemsMap}
                  onItemSelect={onItemSelect}
                />

                {optionsToCreatePage && optionsToCreatePage.length > 0 && (
                  <React.Fragment>
                    <Box paddingTop="spacing-2-xs" paddingLeft="spacing-m" paddingRight="spacing-m">
                      <Paragraph appearance="medium-emphasis">
                        {captions.pageTemplatesSection}
                      </Paragraph>
                    </Box>

                    {optionsToCreatePage.map((item) => (
                      <PageManagerTemplateItem
                        item={item}
                        key={item.name}
                        onItemClick={onTemplateItemClick}
                        captions={{
                          hoveredControl: captions.hoveredTemplateControl,
                        }}
                      />
                    ))}
                  </React.Fragment>
                )}
              </React.Fragment>
            ) : (
              <div className="ws-page-manager__empty-state">
                <Stack gap="spacing-3-xs">
                  <Heading type="heading-2" appearance="low-emphasis" align="center">
                    {captions.searchEmptyTitle}
                  </Heading>
                  <Paragraph size="large" appearance="low-emphasis" align="center">
                    {captions.searchEmptyText}
                  </Paragraph>
                </Stack>
              </div>
            )}
          </Panel>

          {upgradeBanner && (
            <Panel className="ws-page-manager__banner-container">
              <Box
                margin="spacing-m"
                marginTop={upgradeBanner.withProgressBar ? 'spacing-s' : 'spacing-2-xs'}
                marginBottom={upgradeBanner.withProgressBar ? 'spacing-m' : 'spacing-2-xs'}
              >
                <LimitBanner
                  current={upgradeBanner.current}
                  total={upgradeBanner.total}
                  onButtonClick={upgradeBanner.onUpgrade}
                  captions={{
                    title: upgradeBanner.title,
                    buttonText: upgradeBanner.buttonText,
                  }}
                  withProgressBar={upgradeBanner.withProgressBar}
                  withUpgrade={upgradeBanner.withUpgrade}
                />
              </Box>
            </Panel>
          )}
        </PanelGroup>
        {isPageCreationAvailable && (
          // TODO: Remove this code and use below one when folder creation will be implemented
          <FloatingAction
            icon="plus"
            inverseOffsetOrigin
            offsetX={24}
            offsetY={floatingButtonOffsetY}
            onClick={onAddNewPage}
          />
          // TODO: Uncomment when folder creation will be implemented
          // <Dropdown
          //   offsetMainAxis={-(floatingButtonOffsetY + FLOATING_BUTTON_SIZE)}
          //   offsetCrossAxis={-24}
          //   zIndex={1000}
          //   placement="bottom-right"
          //   onClick={(e) => e.stopPropagation()}
          // >
          //   <DropdownTrigger>
          //     <FloatingAction
          //       icon="plus"
          //       inverseOffsetOrigin
          //       offsetX={24}
          //       offsetY={floatingButtonOffsetY}
          //     />
          //   </DropdownTrigger>
          //   <DropdownContent>
          //     <ActionList withMobileBorder>
          //       <ActionListItem
          //         onMouseDown={stopPropagation}
          //         onClick={onCreateFolder}
          //         title={captions.createFolder}
          //         iconGlyph="folder-plus"
          //         iconPlacement="left"
          //       />
          //       <ActionListItem
          //         onMouseDown={stopPropagation}
          //         onClick={onAddNewPage}
          //         title={captions.addNewPage}
          //         iconGlyph="page"
          //         iconPlacement="left"
          //       />
          //     </ActionList>
          //   </DropdownContent>
          // </Dropdown>
        )}
      </Modal>
    </div>
  );
};

PageManager.propTypes = {
  className: PropTypes.string,
  styles: PropTypes.string,
  onItemClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddNewPage: PropTypes.func.isRequired,
  onCreateFolder: PropTypes.func.isRequired,
  filter: PropTypes.func.isRequired,
  isPageCreationAvailable: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object),
  captions: PropTypes.shape({
    searchPlaceholder: PropTypes.string,
    addPage: PropTypes.string,
    cancel: PropTypes.string,
    select: PropTypes.string,
    batchDelete: PropTypes.string,
    setAsHomePage: PropTypes.string,
    fullscreenTooltip: PropTypes.string,
    title: PropTypes.string,
    searchEmptyTitle: PropTypes.string,
    searchEmptyText: PropTypes.string,
    pageTemplatesSection: PropTypes.string,
    hoveredTemplateControl: PropTypes.string,
    createFolder: PropTypes.string,
    addNewPage: PropTypes.string,
    pages: PropTypes.string,
    selected: PropTypes.string,
    noItemsSelectedTooltip: PropTypes.string,
  }).isRequired,
  onSearchClick: PropTypes.func,
  optionsToCreatePage: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  upgradeBanner: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
    onUpgrade: PropTypes.func,
    withProgressBar: PropTypes.bool,
    withUpgrade: PropTypes.bool,
    title: PropTypes.string,
    buttonText: PropTypes.string,
  }),
  onTemplateItemClick: PropTypes.func.isRequired,
  bulkPagesActions: PropTypes.arrayOf(PropTypes.shape({})),
  isAllItemsSelected: PropTypes.bool,
  selectedItemsMap: PropTypes.shape({}),
  onItemSelect: PropTypes.func,
  onAllItemsSelect: PropTypes.func,
  onCancelSelection: PropTypes.func,
};

export default PageManager;
